import { useQuery, useMutation, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { authService } from "@/core/services/auth-service";
import type { User } from "@supabase/supabase-js";

export interface Profile {
  profile_id: number;
  name: string;
  createdAt: string;
  estimatedMonthlyUsdIncome: number | null;
}

export interface UpdateProfileFields {
  estimatedMonthlyUsdIncome: number | null;
}

/**
 * React Query hook that returns the profile for the authenticated user.
 *
 * `authService.createOrUpdateProfile` upserts the profile row on first login,
 * so using it as the query function keeps the "get or create" behavior of the
 * legacy fetch-in-effect. The query is disabled until a user id is present.
 */
export const useProfileQuery = (user: User | null): UseQueryResult<Profile, Error> => {
  return useQuery<Profile, Error>({
    queryKey: ["profile", user?.id],
    queryFn: () => {
      if (!user) {
        throw new Error("No authenticated user");
      }
      return authService.createOrUpdateProfile(user);
    },
    enabled: !!user?.id,
  });
};

/**
 * Persists profile fields and invalidates the profile cache key so consumers
 * of `useProfileQuery` (and `useSession`) reflect the updated value.
 */
export const useUpdateProfileMutation = (profileId: number | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (fields: UpdateProfileFields) => {
      if (profileId === null) {
        return Promise.reject(new Error("No profile id available"));
      }
      // MIG-4: the API addresses the profile from the token, so no profileId.
      return authService.updateProfile(fields);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile"] });
    },
  });
};
